import { useCallback, useMemo, useRef, useState } from 'react';
import useStateWithRef from 'hooks/useStateWithRef';
import { useDisplayUnits } from 'state/displayUnits/context';
import { usePageData } from 'state/pageData';
import { useWidget } from 'state/widget/context';
import { WidgetTracking } from '../types';
import useTrackEvent from './useTrackEvent';
import { getResultProp, isSponsored } from './utils';

export default function useWidgetEventTracking(): WidgetTracking {
  const trackEvent = useTrackEvent();
  const { widgetId } = usePageData();
  const [
    viewportTracked,
    setViewportTracked,
    viewportTrackedRef,
  ] = useStateWithRef(false);
  const {
    state: { units = {} },
  } = useWidget();
  const { displayUnits } = useDisplayUnits();

  const loadTrackedRef = useRef(false);
  const engagementTrackedRef = useRef(false);
  const playedUnitsRef = useRef<string[]>([]);
  const [activeWidgetId, setActiveWidgetId] = useState<string>(widgetId);

  // widgetId shouldn't really change but if it does then these events are
  // associated with a different widget and should fire again
  if (activeWidgetId !== widgetId) {
    setActiveWidgetId(widgetId);
    setViewportTracked(false);
    engagementTrackedRef.current = false;
  }

  const displayedResults = useMemo(
    () => displayUnits.map((resultId) => getResultProp(resultId, units)),
    [displayUnits, units],
  );

  const trackWidgetLoaded = useCallback(() => {
    if (!loadTrackedRef.current) {
      loadTrackedRef.current = true;
      trackEvent({
        event: 'widgetLoad',
        properties: { displayedResults },
      });
    }
  }, [displayedResults, trackEvent]);

  const trackWidgetInViewport = useCallback(() => {
    if (!viewportTrackedRef.current) {
      setViewportTracked(true);
      trackEvent({
        event: 'widgetInViewport',
        properties: { displayedResults },
      });
    }
  }, [displayedResults, setViewportTracked, trackEvent, viewportTrackedRef]);

  const trackWidgetEngaged = useCallback(() => {
    if (!engagementTrackedRef.current) {
      engagementTrackedRef.current = true;
      trackEvent({
        event: 'widgetEngaged',
        properties: { displayedResults },
      });
    }
    trackWidgetInViewport();
  }, [displayedResults, trackEvent, trackWidgetInViewport]);

  const trackUnitPlayed = useCallback(
    (resultId: string) => {
      if (!playedUnitsRef.current.includes(resultId)) {
        playedUnitsRef.current.push(resultId);
        trackEvent({
          event: isSponsored(resultId, units)
            ? 'spResultPlayed'
            : 'resultPlayed',
          properties: {
            playedResult: getResultProp(resultId, units),
          },
        });
      }
      trackWidgetEngaged();
    },
    [trackEvent, trackWidgetEngaged, units],
  );

  const trackUnitClickThrough = useCallback(
    (resultId: string) => {
      trackEvent({
        event: isSponsored(resultId, units)
          ? 'spResultClickThrough'
          : 'resultClickThrough',
        properties: {
          ctResult: getResultProp(resultId, units),
        },
      });
      trackWidgetEngaged();
    },
    [trackEvent, trackWidgetEngaged, units],
  );

  return useMemo(
    () => ({
      trackUnitClickThrough,
      trackUnitPlayed,
      trackWidgetEngaged,
      trackWidgetInViewport,
      trackWidgetLoaded,
      viewportTracked,
    }),
    [
      trackUnitClickThrough,
      trackUnitPlayed,
      trackWidgetEngaged,
      trackWidgetInViewport,
      trackWidgetLoaded,
      viewportTracked,
    ],
  );
}
